import userflow, { EventAttributes } from 'userflow.js';
import { hasSubscription, isInFreeTrial, isOnFreePlan, user } from '@/ui/auth/auth';
import { LoggedInUser } from '@/entities/auth/loggedInUser';

export const USERFLOW_MOBILE_WIDTH = 768;

export enum UserflowTrackEvent {
  TEMPLATE_CREATED = 'template_created',
  USER_INVITED = 'user_invited',
  ACTION_CREATED = 'first_action_created',
  FORM_SUBMITTED = 'first_form_submission',
  APP_INSTALLED = 'app_installed',
}

export enum UserflowTrackEventData {
  TEMPLATE_NAME = 'template-name'
}

enum UserflowPlan {
  FREE = 'free',
  TRIAL = 'trial',
  PAID = 'paid',
}

export enum UserflowTriggers {
  MOBILE_APP_POPUP = 'showMobileDownloadPopup',
  SHOW_YOUTUBE_VIDEO = 'showYoutubeVideo',
  OPEN_REQUEST_FEATURE_MODAL = 'openRequestFeatureModal',
  OPEN_REPORT_ISSUE_MODAL = 'openReportIssueModal',
}

let initialized = false;
let identifiedUser = { id: 0, language: 'en' };
const USERFLOW_TOKEN = String(process.env.VUE_APP_USERFLOW_TOKEN);

function boot() {
  userflow.init(USERFLOW_TOKEN);
  initialized = true;

  if (user.value && user.value.id !== 0) {
    identify(user.value);
  }
}

const getUserPlan = (): UserflowPlan|null => {
  switch (true) {
    case isOnFreePlan.value:
      return UserflowPlan.FREE;
    case isInFreeTrial.value:
      return UserflowPlan.TRIAL;
    case hasSubscription():
      return UserflowPlan.PAID;
    default:
      return null;
  }
};

function identify(user: LoggedInUser) {
  if (user.id === 0) {
    return;
  }

  if (!initialized) {
    boot();

    return;
  }

  if (identifiedUser.id === user.id && identifiedUser.language === user.locale) {
    return;
  }

  identifiedUser = { id: user.id, language: user.locale };
  userflow.identify(String(user.id), {
    user_id: user.id,
    name: user.name,
    email: user.email,
    language: user.locale,
    locale_code: user.locale,
    is_organization_admin: user.admin,
    is_first_user: user.organization.firstUserId === user.id,
    signed_up_at: user.createdAt.toISOString(),
    plan: getUserPlan(),
    device_type: window.innerWidth > USERFLOW_MOBILE_WIDTH ? 'desktop' : 'mobile',
  });
}

function trackEvent(event: UserflowTrackEvent, data: EventAttributes = {}, isUserOnly = true) {
  if (!isUserflowEnabled()) {
    return;
  }

  if (!initialized) {
    boot();

    return;
  }

  if (identifiedUser) {
    userflow.track(event, data, { userOnly: isUserOnly });
  }
}

function isUserflowEnabled() {
  return process.env.VUE_APP_USERFLOW_ENABLED === 'true';
}

async function startTour(tourId: string) {
  return userflow.start(tourId);
}

export const Userflow = {
  boot,
  identify,
  isUserflowEnabled,
  trackEvent,
  startTour,
};
